import {
    AssessmentModule,
    CultureFitScores,
    InterviewScores,
    MentalTestScores,
    Score,
    ScoresAvailability,
    WorkPreferenceBaselineData,
} from './typings';

export function getDateForDocuments(date: Date, language: 'nl' | 'en'): string {
    const monthNames = {
        en: [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ],
        nl: [
            'Januari',
            'Februari',
            'Maart',
            'April',
            'Mei',
            'Juni',
            'Juli',
            'Augustus',
            'September',
            'Oktober',
            'November',
            'December',
        ],
    };
    const day = date.getDate();
    const monthIndex = date.getMonth();
    const year = date.getFullYear();

    return day + ' ' + monthNames[language][monthIndex] + ' ' + year;
}

export const getExpectedScores = (
    assessmentModules: AssessmentModule[],
    isCultureFitBaselineAvailable: boolean,
): ScoresAvailability => {
    const scoresAvailability: ScoresAvailability = {
        cultureFit: {
            companyEngagement: false,
            workPreferences: false,
            cultureMatch: false,
        },
        speaking: {
            motivation: false,
            confidence: false,
        },
        cognitiveAbilities: {
            stressResilience: false,
            ease: false,
            categories: false,
        },
    };

    for (const module of assessmentModules) {
        if (
            [
                'terms-and-conditions',
                'allow-camera-access',
                'feedback-questionnaire',
                'assessment-upload-progression',
                'assessment-ending',
                'data-collection-scan',
            ].includes(module.id)
        ) {
            continue;
        }
        switch (module.id) {
            case 'culture-fit':
                scoresAvailability.cultureFit.companyEngagement = true;
                break;
            case 'culture-fit-questionnaire':
                scoresAvailability.cultureFit.workPreferences = true;
                if (isCultureFitBaselineAvailable) {
                    scoresAvailability.cultureFit.cultureMatch = true;
                }
                break;
            case 'interview':
                scoresAvailability.speaking = {
                    motivation: true,
                    confidence: true,
                };
                break;
            case 'mental-test':
                scoresAvailability.cognitiveAbilities = {
                    stressResilience: true,
                    ease: true,
                    categories: true,
                };
        }
    }

    return scoresAvailability;
};

export const getScores = (
    scores: Score[],
    key: 'culture_fit' | 'interview' | 'mental_test',
): CultureFitScores | InterviewScores | MentalTestScores | undefined => {
    if (scores && scores.length) {
        return scores.find((score) => score.module_id === key)?.data[key];
    }
};

export const getCultureFitScores = (
    scores: Score[],
): CultureFitScores | undefined => {
    const cfScores = getScores(scores, 'culture_fit') as CultureFitScores;

    if (cfScores) {
        if (
            'company_engagement' in cfScores ||
            'culture_match' in cfScores ||
            ('categories' in cfScores && !('stress_resilience' in cfScores))
        ) {
            return cfScores;
        }
    }
};

export const getSpeakingScores = (
    scores: Score[],
): InterviewScores | undefined => {
    const speakingScores = getScores(scores, 'interview') as InterviewScores;

    if (speakingScores) {
        if (
            'work_motivation' in speakingScores ||
            'confidence' in speakingScores
        ) {
            return speakingScores;
        }
    }
};

export const getCognitiveAbilitiesScores = (
    scores: Score[],
): MentalTestScores | undefined => {
    const caScores = getScores(scores, 'mental_test') as MentalTestScores;

    if (caScores) {
        if (
            'stress_resilience' in caScores ||
            'ease' in caScores ||
            'categories' in caScores
        ) {
            return caScores;
        }
    }
};

export const roundScore = (number: number | undefined) => {
    if (!number) {
        number = 0;
    }
    return Math.trunc(number);
};

export const getBaselineScoreRange = (
    baseline: WorkPreferenceBaselineData,
    score: 'market' | 'adhocracy' | 'hierarchy' | 'family',
): [number, number] => {
    const getBaselineMinMax = (data: number[]): [number, number] => {
        const k = 1.28;
        const count = data.length;
        const mean = data.reduce((a, b) => a + b, 0) / count;

        const standardDeviation = Math.sqrt(
          data
            .map((score) => Math.pow(score - mean, 2))
            .reduce((a, b) => a + b, 0) /
          (count - 1),
        );

        return [mean - k * standardDeviation, mean + k * standardDeviation];
    };

    const baselineScoreRange = getBaselineMinMax(
        baseline.map((data) => data[score]),
    );
    return [
        roundScore((baselineScoreRange[0] / 16) * 100),
        roundScore((baselineScoreRange[1] / 16) * 100),
    ];
};
